import { useEffect, useState } from 'react';
import './Navbar.css';
import Icon from './Icon';
import { CALENDLY_URL } from '../config';
import { SESSIONS } from '../data/sessions';

// §4.2 anchor mapping: About→#about · Programs→#programs · Methodology→#approach ·
// Why us→#why-us · For colleges→#for-colleges · Testimonials→#testimonials · Contact→#contact
// The Sessions link is a real route (/sessions) and only appears once photos exist.
// On subpages, in-page anchors are prefixed with "/" so they navigate home first
// (LandingPage's mount effect reads the hash on load and scrolls to the section).
const onHome = typeof window !== 'undefined' && window.location.pathname === '/';
const toHref = (anchor) => (onHome ? anchor : `/${anchor}`);
const brandHref = onHome ? '#top' : '/';
const LINKS = [
  { label: 'Methodology', href: toHref('#approach') },
  ...(SESSIONS.length > 0 ? [{ label: 'Programs we offer', href: '/sessions' }] : []),
  { label: 'Testimonials', href: toHref('#testimonials') },
  { label: 'Contact Us', href: toHref('#contact') },
];

export default function Navbar() {
  // On subpages there is no hero CTA to observe, so keep the nav CTA visible from the start.
  const [ctaVisible, setCtaVisible] = useState(!onHome);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // §4.1 — observe hero CTA; nav CTA visible when hero CTA leaves viewport.
  useEffect(() => {
    const target = document.getElementById('hero-cta');
    if (!target || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setCtaVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, []);

  // §7.4 — sticky-nav shadow once scrollY > 8.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // §4.3 — mobile menu closes on link tap.
  const handleLinkClick = () => setMenuOpen(false);

  return (
    <header className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
      <div className="nav__inner container">
        <a href={brandHref} className="nav__brand" aria-label="WAYFWD Strategies — home">
          <img
            src="/assets/logo.png"
            alt=""
            className="nav__logo"
            width="36"
            height="36"
            aria-hidden="true"
          />
          <span className="nav__wordmark">WAYFWD Strategies</span>
        </a>

        <button
          className="nav__toggle"
          type="button"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="nav-menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <Icon name={menuOpen ? 'close' : 'menu'} size={22} />
        </button>

        <nav
          id="nav-menu"
          className={`nav__menu ${menuOpen ? 'is-open' : ''} ${ctaVisible ? 'nav__menu--centered' : ''}`.trim()}
          aria-label="Primary"
        >
          <ul className="nav__links">
            {LINKS.map((link) => (
              <li key={link.href}>
                <a href={link.href} onClick={handleLinkClick} className="nav__link">
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          id="nav-cta"
          className={`btn btn-primary nav__cta ${ctaVisible ? 'is-visible' : ''}`}
          onClick={handleLinkClick}
        >
          Book a complimentary call
        </a>
      </div>
    </header>
  );
}
